// Wire contract — the device-targets read (the collector work-list).
// Contract home: DATA-CONTRACTS §device targets; model: ADR-063 §access facet.
// Machine consumers only (ApiKey actor holding device_targets:read) — the
// platform's ONE bulk-plaintext door: secret fields serve as PLAINTEXT over
// TLS, narrowed per credential by api_clients.device_target_kinds (an entry
// travels whole or not at all; empty list = identity-only). This schema is the
// single place plaintext secret fields appear on any response shape — every
// other surface serves markers (`secrets_set`) or the audited per-field reveal.
//
// Shape law: one item per ENDPOINT — the dialable chain-root unit with its
// address and its own services, every unit reached through it in a FLAT
// `fronted` list (any depth), each with its own service params as-dialed at
// the root. The reach facts (vpn flavor · SIM slot records) carry zero secrets
// by design and always serve — they are the identity layer every key tier
// gets (the trap receiver's sender resolution: source address → item →
// candidate set, matching overlay AND declared APN addresses).
//
// Declared truth only: no protocol port defaults are synthesized (161/22/…
// are the consumer's protocol knowledge), `local_port` never serves (display +
// on-site truth — machines dial `port` on the root address), and nothing
// context-shaped rides here (names, serials, site facts = the projections).
import { z } from 'zod';
import { AssetCategorySchema } from '../assets/categories.js';
import { SimSlotSchema, SNMP_AUTH_PROTOCOLS, SNMP_PRIV_PROTOCOLS, VPN_FLAVORS, WEB_SCHEMES, } from '../assets/access.js';
const port = z.number().int().min(1).max(65535);
const plain = z.string(); // an unsealed secret — plaintext on THIS wire only
// ── service entries — the credential-bearing half, narrowed per credential ──
export const TargetSnmpV1Schema = z.object({
    kind: z.literal('snmp_v1'),
    port: port.optional(),
    community_read: plain.optional(),
    community_write: plain.optional(),
    community_trap: plain.optional(),
});
export const TargetSnmpV2cSchema = TargetSnmpV1Schema.extend({ kind: z.literal('snmp_v2c') });
export const TargetSnmpV3Schema = z.object({
    kind: z.literal('snmp_v3'),
    port: port.optional(),
    username: z.string(),
    auth_protocol: z.enum(SNMP_AUTH_PROTOCOLS).optional(),
    auth_key: plain.optional(),
    priv_protocol: z.enum(SNMP_PRIV_PROTOCOLS).optional(),
    priv_key: plain.optional(),
});
export const TargetModbusTcpSchema = z.object({
    kind: z.literal('modbus_tcp'),
    port: port.optional(),
    unit_id: z.number().int().min(0).max(255).optional(),
});
export const TargetModbusRtuSchema = z.object({
    kind: z.literal('modbus_rtu'),
    unit_id: z.number().int().min(0).max(255).optional(),
    baud_rate: z.number().int().positive().optional(),
});
export const TargetWebSchema = z.object({
    kind: z.literal('web'),
    scheme: z.enum(WEB_SCHEMES).optional(),
    port: port.optional(),
    username: z.string().optional(),
    password: plain.optional(),
});
export const TargetSshSchema = z.object({
    kind: z.literal('ssh'),
    port: port.optional(),
    username: z.string().optional(),
    password: plain.optional(),
});
export const TargetFtpSchema = z.object({
    kind: z.literal('ftp'),
    port: port.optional(),
    username: z.string().optional(),
    password: plain.optional(),
});
export const TargetServiceSchema = z.discriminatedUnion('kind', [
    TargetSnmpV1Schema,
    TargetSnmpV2cSchema,
    TargetSnmpV3Schema,
    TargetModbusTcpSchema,
    TargetModbusRtuSchema,
    TargetWebSchema,
    TargetSshSchema,
    TargetFtpSchema,
]);
// ── the reach block — zero secrets by construction; serves on every tier ────
export const TargetReachSchema = z
    .object({
    vpn: z.object({ flavor: z.enum(VPN_FLAVORS) }).optional(),
    cellular: z.object({ sims: z.array(SimSlotSchema) }).optional(),
})
    .strict();
// ── units ───────────────────────────────────────────────────────────────────
const unitCore = {
    asset_id: z.string(),
    site_id: z.string(), // deployed ⇔ site (never a warehouse row)
    reference: z.string(), // the register's own key — operator-log labels
    canonical_key: z.string().nullable(), // the consumer's dictionary binding; NULL = not yet monitorable
    category: AssetCategorySchema,
    reach: TargetReachSchema,
    services: z.array(TargetServiceSchema),
};
/** A unit dialed THROUGH the item's root: its `services` ports are as-dialed
 *  at the root address (the register stores them so — no composition math). */
export const TargetFrontedUnitSchema = z.object(unitCore).strict();
/** One endpoint = one item: the chain-root unit (own address) + everything
 *  reached through it, flat. A Direct device is an item with empty `fronted`. */
export const TargetItemSchema = z
    .object({
    ...unitCore,
    address: z.string(), // the dial address — the root's device_ip (the overlay IP in cellular estates)
    fronted: z.array(TargetFrontedUnitSchema),
})
    .strict();
// The envelope mirrors the projections grammar; `version` is the ETag's opaque
// token (the devices + sites counters combined) — compare, never parse.
export const DeviceTargetsResponseSchema = z
    .object({
    version: z.string(),
    items: z.array(TargetItemSchema),
})
    .strict();
